import { EventPayload, QiraEventKind } from 'api3';
import { Injectable, inject } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { GoogleTagService } from '@app/services/google-tag-manager.service';
import { AuthService } from './auth.service';
import { environment } from '@env';
import * as Sentry from '@sentry/angular';

export enum QiraEventHumanReadable {
  PAGE_ROUTING = 100000,
  VEHICLE_IDENTIFICATION_START = 300000,
  VEHICLE_IDENTIFICATION_TRIGGERED = 300100,
  VEHICLE_IDENTIFIED = 300200,
  VEHICLE_UNAVAILABLE_KBA = 300210,
  VEHICLE_UNAVAILABLE_VIN = 300220,
  USER_CLICK_THREAD_TAB = 340000,
  USER_CLICK_CLOSE_THREAD = 300300,
  USER_TEXT_FEEDBACK_SEND = 330000,
  //USER_TEXT_FEEDBACK_CANCELLED = 330110,
  USER_START_QUESTION = 310000,
  USER_SEND_QUESTION = 310010,
  LOGOUT_DEVICE = 212000,
  DEVICE_CODE_INVALIDATED_BY_ADMIN = 212010,
}

@Injectable({
  providedIn: 'root',
})
export class EventTrackerService {
  authService = inject(AuthService);
  googleTagService = inject(GoogleTagService);
  api3Service = inject(Api3Service);

  eventTrackingEnabled = environment.enableEventTracking;

  public async logEvent(qiraEvent: QiraEventHumanReadable, payload?: EventPayload) {
    if (!this.eventTrackingEnabled) return;

    const userIsLoggedIn = await this.authService.isLoggedIn();
    if (!userIsLoggedIn) {
      console.warn('[EventTrackerService] User is not logged in. Event not logged:', qiraEvent, payload);
      return;
    }

    const qiraEventString = QiraEventKind[`Value${qiraEvent}` as keyof typeof QiraEventKind];
    if (!qiraEventString) {
      console.error('[EventTrackerService] Invalid QiraEventKind:', qiraEvent);
      Sentry.captureException(new Error('Invalid QiraEventKind'), {
        tags: {
          location: 'EventTrackerService',
          feature: 'EventTracker',
          message: 'Event logging was called with invalid QiraEventKind key.',
        },
        extra: {
          qiraEvent,
          payload,
        },
      });
      return;
    }

    const { error } = await this.api3Service.api.eventTracker.logEvent({
      event: {
        kind: qiraEventString,
        payload: { ...payload },
      },
    });

    if (error) {
      console.error('[EventTrackerService] API-Error while calling logEvent().', qiraEvent, payload);
      Sentry.captureException(error, {
        tags: {
          location: 'EventTrackerService',
          feature: 'EventTracker',
          message: 'API-Error while calling logEvent().',
        },
        extra: {
          qiraEvent,
          payload,
        },
      });
    }

    this.googleTagService.push(qiraEvent, payload);
  }
}
